/**
 * Analytics helper — GA4 event dispatch with Consent Mode v2 awareness.
 *
 * ARCHITECTURE:
 *   - gtag.js is loaded unconditionally in public/index.html but with
 *     Consent Mode v2 default-denied for all storage flags. That means
 *     Google still receives modeled/anonymous signal but never sets
 *     cookies or identifiers until the visitor Accepts the cookie
 *     banner (which calls gtag('consent','update', ... 'granted')).
 *   - trackEvent() below just calls gtag('event', ...). We do NOT gate
 *     event dispatch on consent state — Google Consent Mode is designed
 *     so that events sent in denied-mode still contribute to modeled
 *     conversion aggregates without persisting anything about the user.
 *     Suppressing events client-side would only lose signal without
 *     improving privacy.
 *   - Env vars: REACT_APP_GA_MEASUREMENT_ID (G-XXXXXXXXXX). If unset,
 *     helper no-ops with a console warning — same defensive pattern as
 *     src/utils/googleAds.js. Never throws.
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

/**
 * @typedef {Object} TrackEventOptions
 * @property {number} [value]          - Monetary value (event_value / GA4 `value`)
 * @property {string} [currency]       - 3-letter ISO currency, default EUR
 * @property {string} [transaction_id] - For `purchase` events — must be stable per booking
 * @property {Record<string, any>} [params] - Any additional GA4 event params
 */

/**
 * Fire a GA4 event. Guarded — no-ops when gtag isn't loaded or
 * REACT_APP_GA_MEASUREMENT_ID isn't set, so it's safe to call from
 * any code path (SSR, tests, misconfigured deploys).
 *
 * @param {string} eventName - GA4 event name (snake_case). Prefer the
 *   standard recommended names (`begin_checkout`, `add_payment_info`,
 *   `purchase`) so GA4's ecommerce reports light up without custom
 *   dimension config. Custom names OK too — GA4 auto-registers them.
 * @param {TrackEventOptions} [opts]
 */
export const trackEvent = (eventName, opts = {}) => {
  if (typeof window === "undefined") return false;
  if (!window.gtag) {
    // gtag.js still loading — this can happen if trackEvent fires
    // before the async <script> resolves on very fast page loads.
    console.debug("[ga4] gtag not ready — event dropped:", eventName);
    return false;
  }
  if (!GA_ID) {
    console.info("[ga4] REACT_APP_GA_MEASUREMENT_ID not set — event skipped:", eventName);
    return false;
  }

  const { value, currency = "EUR", transaction_id, params = {} } = opts;
  const payload = { send_to: GA_ID, ...params };
  if (value !== undefined) payload.value = Number(value) || 0;
  if (currency) payload.currency = currency;
  if (transaction_id) payload.transaction_id = String(transaction_id);

  window.gtag("event", eventName, payload);
  console.debug(`[ga4] event ${eventName}`, payload);
  return true;
};

/**
 * Convenience wrappers for the standard funnel points — keeps callsites
 * short and prevents typos on the event name (which would silently
 * fragment reports in GA4).
 */
export const trackBookingStarted = () => trackEvent("begin_checkout");

export const trackBookingFormSubmitted = ({ value, currency = "EUR" } = {}) =>
  trackEvent("add_payment_info", { value, currency });

/**
 * `purchase` — the true conversion. Guarded against double-fire by a
 * sessionStorage key on the booking_id, matching the pattern in
 * fireGoogleAdsConversion so both the Ads conversion and this GA4
 * purchase event obey the same single-fire discipline.
 */
export const trackBookingPurchase = ({ transaction_id, value, currency = "EUR" }) => {
  if (typeof window === "undefined" || !transaction_id) return false;
  const guardKey = `ga4_purchase_${transaction_id}`;
  try {
    if (sessionStorage.getItem(guardKey)) return false;
    sessionStorage.setItem(guardKey, "1");
  } catch (e) {
    // sessionStorage disabled — best-effort fire, small risk of double
    // counting on the same tab reload; acceptable.
  }
  return trackEvent("purchase", { value, currency, transaction_id });
};

/**
 * Promotion Engine outcome — fired when the /api/bookings/ response
 * includes a promotion object. `outcome_label` is the human-readable
 * result ("10% off", "Free ceramic top-coat", "No prize"), passed
 * through to GA4 as a custom parameter so it can be filtered/segmented
 * in the funnel exploration report.
 */
export const trackPromoEntry = ({ outcome_label, discount_value }) => {
  return trackEvent("promo_entry", {
    params: {
      outcome_label: String(outcome_label || "unknown"),
      discount_value: Number(discount_value) || 0,
    },
  });
};
